import { Clock, Edit, Eye, Loader2, MoreHorizontal, Play, Plus, Power, Search, Target, Trash2 } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AdminOnlyEmptyState } from '@/components/auth/admin-only-empty-state';
import { useIsAdmin } from '@/components/auth/useIsAdmin';
import { HelpSupportButton } from '@/components/help-support';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  useDeleteStrategy,
  useRunStrategyNow,
  useStrategyTypeOptions,
  useToggleStrategyStatus,
} from '@/hooks/useStrategiesData';
import { useStrategiesListGraphQL } from '@/hooks/useStrategiesListGraphQL';
import type { CollectionStrategyItem } from '@/types/collection-strategies';

interface StrategiesPageProps {
  onCreateStrategy?: () => void;
  onEditStrategy?: (strategy: CollectionStrategyItem) => void;
}

const StrategiesPage: React.FC<StrategiesPageProps> = ({ onCreateStrategy, onEditStrategy }) => {
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [, setTogglingStrategyId] = useState<string | null>(null);
  // setTogglingStrategyId available for future loading state UI
  void setTogglingStrategyId;

  const toggleStatusMutation = useToggleStrategyStatus();
  const deleteStrategyMutation = useDeleteStrategy();
  const runNowMutation = useRunStrategyNow();

  // Lookup data for dropdowns (cached for 1 minute)
  const { data: strategyTypeOptions = [] } = useStrategyTypeOptions();

  // Fetch strategies via GraphQL with search/filters applied server-side
  const { data: strategies = [], isLoading } = useStrategiesListGraphQL({
    search: searchQuery || undefined,
    type: filterType !== 'all' ? filterType : undefined,
    status: filterStatus !== 'all' ? filterStatus : undefined,
  });

  const handleCreateStrategy = () => {
    if (onCreateStrategy) {
      onCreateStrategy();
    } else {
      navigate('/settings/strategies/create', { state: { mode: 'create' } });
    }
  };

  const handleEditStrategy = (strategy: CollectionStrategyItem) => {
    if (onEditStrategy) {
      onEditStrategy(strategy);
    } else {
      navigate(`/settings/strategies/edit?id=${strategy.id}`, { state: { strategy, mode: 'edit' } });
    }
  };

  const handleViewPerformance = (strategy: CollectionStrategyItem) => {
    if (onEditStrategy) {
      onEditStrategy(strategy);
    } else {
      navigate(`/settings/strategies/edit?id=${strategy.id}&tab=performance`, { state: { strategy, mode: 'edit' } });
    }
  };

  const handleToggleStatus = async (strategy: CollectionStrategyItem) => {
    const newStatus = strategy.status === 'Active' ? 'Inactive' : 'Active';
    setTogglingStrategyId(strategy.id);
    try {
      await toggleStatusMutation.mutateAsync({
        id: strategy.id,
        status: newStatus,
      });
      toast.success(`Strategy ${newStatus === 'Active' ? 'activated' : 'deactivated'} successfully`);
    } catch {
      toast.error('Failed to update strategy status');
    } finally {
      setTogglingStrategyId(null);
    }
  };

  const handleDeleteStrategy = async (strategy: CollectionStrategyItem) => {
    try {
      await deleteStrategyMutation.mutateAsync(strategy.id);
      toast.success('Strategy deleted successfully');
    } catch {
      toast.error('Failed to delete strategy');
    }
  };

  const handleRunNow = async (strategy: CollectionStrategyItem) => {
    try {
      await runNowMutation.mutateAsync(strategy.id);
      toast.success(`Strategy "${strategy.name}" run triggered successfully`);
    } catch {
      toast.error('Failed to trigger strategy run. An active run may already exist.');
    }
  };

  const getTypeBadge = (type: CollectionStrategyItem['type']) => {
    const colors: Record<CollectionStrategyItem['type'], string> = {
      Standard: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      Intensive: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      VIP: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      'High Risk': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    };
    return <Badge className={colors[type]}>{type}</Badge>;
  };

  if (isAdminLoading) {
    return (
      <div className="flex min-h-[50vh] w-full items-center justify-center">
        <output
          aria-label="Loading"
          className="block h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground"
        />
      </div>
    );
  }
  if (!isAdmin) return <AdminOnlyEmptyState />;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight">Collection Strategies</h1>
          <p className="text-muted-foreground mt-2">Configure automated collection workflows and assignment rules</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleCreateStrategy}>
            <Plus className="h-4 w-4 mr-2" />
            Create Strategy
          </Button>
          <HelpSupportButton screenId="settings-strategies" />
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search strategies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {strategyTypeOptions.length > 0 ? (
              strategyTypeOptions.map((option) => (
                <SelectItem key={option.id} value={option.name}>
                  {option.name}
                </SelectItem>
              ))
            ) : (
              <>
                <SelectItem value="Standard">Standard</SelectItem>
                <SelectItem value="Intensive">Intensive</SelectItem>
                <SelectItem value="VIP">VIP</SelectItem>
                <SelectItem value="High Risk">High Risk</SelectItem>
              </>
            )}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Strategies Table */}
      <Card>
        <CardHeader>
          <CardTitle>Strategies ({strategies.length})</CardTitle>
          <CardDescription>Manage your collection strategies and monitor their performance</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name & Description</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Open Cases</TableHead>
                <TableHead>Avg Days</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      <span className="text-muted-foreground">Loading strategies...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : strategies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No strategies found matching your criteria
                  </TableCell>
                </TableRow>
              ) : (
                strategies.map((strategy) => (
                  <TableRow key={strategy.id}>
                    <TableCell>
                      <div className="cursor-pointer group" onClick={() => handleEditStrategy(strategy)}>
                        <div className="font-medium text-primary hover:underline group-hover:text-primary/80 transition-colors">
                          {strategy.name}
                        </div>
                        <div className="text-sm text-muted-foreground max-w-md truncate group-hover:text-muted-foreground/80 transition-colors">
                          {strategy.description}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getTypeBadge(strategy.type)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={strategy.status === 'Active'}
                          onCheckedChange={() => handleToggleStatus(strategy)}
                        />
                        <span className="text-sm">{strategy.status === 'Active' ? 'Active' : 'Inactive'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4 text-muted-foreground" />
                        {strategy.openCasesCount}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        {strategy.avgDaysOpen} days
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEditStrategy(strategy)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit Strategy
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleViewPerformance(strategy)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Performance
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleStatus(strategy)}>
                            <Power className="h-4 w-4 mr-2" />
                            {strategy.status === 'Active' ? 'Deactivate' : 'Activate'}
                          </DropdownMenuItem>
                          {strategy.status === 'Active' && (
                            <DropdownMenuItem onClick={() => handleRunNow(strategy)}>
                              <Play className="h-4 w-4 mr-2" />
                              Run Now
                            </DropdownMenuItem>
                          )}
                          {strategy.openCasesCount === 0 && strategy.status === 'Inactive' && (
                            <DropdownMenuItem
                              onClick={() => handleDeleteStrategy(strategy)}
                              className="text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default StrategiesPage;
